import { API_BASE_URL } from './utils';
import type { EventMapDrawing } from '../types';

export type { EventMapDrawing };

export function mapDrawingsStorageKey(eventId: string): string {
  return `c2_map_drawings_${eventId}`;
}

export function loadLocalMapDrawings(eventId: string | null | undefined): EventMapDrawing[] {
  if (!eventId) return [];
  try {
    const stored = localStorage.getItem(mapDrawingsStorageKey(eventId));
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearLocalMapDrawings(eventId: string | null | undefined): void {
  if (!eventId) return;
  try {
    localStorage.removeItem(mapDrawingsStorageKey(eventId));
  } catch {
    /* ignore */
  }
}

export async function fetchEventMapDrawings(
  eventId: string,
  token: string
): Promise<EventMapDrawing[]> {
  const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/map-drawings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load map drawings');
  }
  const data = await res.json();
  return Array.isArray(data.drawings) ? data.drawings : [];
}

export async function saveEventMapDrawingsApi(
  eventId: string,
  token: string,
  drawings: EventMapDrawing[]
): Promise<EventMapDrawing[]> {
  const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/map-drawings`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ drawings }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save map drawings');
  }
  const data = await res.json();
  return Array.isArray(data.drawings) ? data.drawings : [];
}

export async function deleteEventMapDrawingsApi(eventId: string, token: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/map-drawings`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to clear map drawings');
  }
}

/** One-time migration: upload browser-local drawings when server has none. */
export async function migrateLocalMapDrawingsToServer(
  eventId: string,
  token: string
): Promise<EventMapDrawing[] | null> {
  const local = loadLocalMapDrawings(eventId);
  if (local.length === 0) return null;

  const server = await fetchEventMapDrawings(eventId, token);
  if (server.length > 0) return null;

  const saved = await saveEventMapDrawingsApi(eventId, token, local);
  clearLocalMapDrawings(eventId);
  return saved;
}
